import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    {
      code: 'FR',
      name: 'Français',
      flag: '/images/flag-fr.png'
    },
    {
      code: 'EN',
      name: 'English',
      flag: '/images/flag-en.png'
    }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 hover:border-cyan-600/50 hover:bg-zinc-800/80 transition-all duration-300 group"
        data-testid="language-selector-btn"
      >
        <img 
          src={currentLanguage.flag} 
          alt={currentLanguage.name}
          className="w-4 h-4 sm:w-5 sm:h-5 rounded object-cover transition-transform duration-300 group-hover:scale-110"
        />
        <span className="text-xs sm:text-sm text-white font-medium">{currentLanguage.code}</span>
        <ChevronDown 
          className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-all duration-300 ${
            isOpen ? 'rotate-180 text-cyan-400' : 'group-hover:text-cyan-400'
          }`}
        />
      </button>

      {/* Dropdown Menu - Animated */}
      <div className={`absolute top-full right-0 mt-2 w-40 bg-zinc-900/95 backdrop-blur-md border border-zinc-800 rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.5)] overflow-hidden z-50 transition-all duration-300 origin-top ${
        isOpen 
          ? 'opacity-100 scale-100 translate-y-0' 
          : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
      }`}>
        {languages.map((lang, index) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`w-full flex items-center gap-3 px-4 py-3 transition-all duration-200 ${
              language === lang.code
                ? 'bg-cyan-600/20 text-cyan-400 border-l-2 border-cyan-400'
                : 'text-gray-300 hover:bg-zinc-800 hover:text-white hover:translate-x-1'
            }`}
            style={{ transitionDelay: `${index * 50}ms` }}
            data-testid={`lang-${lang.code.toLowerCase()}`}
          >
            <img 
              src={lang.flag} 
              alt={lang.name}
              className="w-6 h-6 rounded object-cover transition-transform duration-200 hover:scale-110"
            />
            <span className="text-sm font-medium">{lang.name}</span>
            {language === lang.code && (
              <span className="ml-auto text-cyan-400 text-xs">✓</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSelector;
